import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Download, Eye, EyeOff } from "lucide-react";
import { generateLegalDoc, generateLegalDocPdf, today, type DocSection } from "./legalDocUtils";
import { LegalDocPreview } from "./LegalDocPreview";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Provides a general-purpose service agreement template for common freelance/contractor business situations, as a starting point to customize for your specific agreement.`,
  how: `Fill in the client, provider, and service details. Preview the agreement, then download it as a Word or PDF file.`,
  tip: `This is a general template, not a substitute for legal advice on anything with significant money or risk involved \u2014 for an important or high-value agreement, having a lawyer review the final version is worth the cost.`,
};

const TITLE = "Service Agreement";

export default function ContractTemplates() {
  const [clientName, setClientName] = useState("");
  const [providerName, setProviderName] = useState("");
  const [serviceDescription, setServiceDescription] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("Net 30 (due within 30 days of invoice)");
  const [state, setState] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [ip, setIp] = useState<"client" | "provider">("client");

  const missingRequired = !clientName.trim() || !providerName.trim() || !serviceDescription.trim() || !paymentAmount.trim() || !state.trim();

  const endClean = (s: string) => s.trim().replace(/[.\s]+$/, ""); // the template adds its own full stop

  const buildSections = (): DocSection[] => [
    {
      paragraphs: [
        `This Service Agreement is made on ${today()}, between ${clientName || "[Client Name]"} ("Client") and ${providerName || "[Service Provider Name]"} ("Provider").`,
      ],
    },
    {
      heading: "1. Services",
      paragraphs: [`Provider agrees to perform the following services for Client: ${endClean(serviceDescription) || "[Description of Services]"}.`],
    },
    {
      heading: "2. Payment",
      paragraphs: [`Client agrees to pay Provider $${paymentAmount || "[Amount]"} for the services described above. Payment terms: ${endClean(paymentTerms)}.`],
    },
    {
      heading: "3. Independent Contractor Status",
      paragraphs: ["Provider is an independent contractor, not an employee of Client. Provider is responsible for their own taxes, insurance, and benefits."],
    },
    {
      heading: "4. Confidentiality",
      paragraphs: ["Both parties agree to keep confidential any proprietary or sensitive information shared during the course of this engagement."],
    },
    {
      heading: "5. Ownership of Work (Intellectual Property)",
      paragraphs: [
        ip === "client"
          ? "On full payment, Provider assigns to Client all rights in the work product created specifically for Client under this Agreement. Provider keeps ownership of its pre-existing materials, tools and general know-how, and grants Client a non-exclusive, perpetual license to use any of those included in the work product."
          : "Provider keeps ownership of all work product and grants Client a non-exclusive, perpetual license to use it for Client's own business purposes. Client does not acquire ownership of Provider's pre-existing materials, tools or general know-how.",
      ],
    },
    {
      heading: "6. Limitation of Liability",
      paragraphs: [
        "Neither party is liable to the other for indirect, incidental, special or consequential damages, or for lost profits. Except for a party's obligation to pay amounts due and breach of the confidentiality clause, each party's total liability under this Agreement is limited to the fees paid or payable under this Agreement.",
      ],
    },
    {
      heading: "7. Changes and Entire Agreement",
      paragraphs: ["Changes to the scope or price must be agreed in writing by both parties. This Agreement is the entire agreement between the parties on its subject and replaces any earlier discussions."],
    },
    {
      heading: "8. Termination",
      paragraphs: ["Either party may terminate this Agreement with 14 days' written notice. Client remains responsible for payment of services rendered up to the termination date."],
    },
    {
      heading: "9. Governing Law",
      paragraphs: [`This Agreement is governed by the laws of ${state || "[State]"}.`],
    },
    {
      heading: "Signatures",
      paragraphs: ["Client: _________________________  Date: _____________", "Provider: _________________________  Date: _____________"],
    },
  ];

  // Was useLogToolUsage(true, 3000, { ownActions: true }) -- a debounced logger with no
  // gating at all, while the real value-delivering actions (the Word/
  // PDF download buttons below) had ZERO usage-limit check of any
  // kind, not even the weak kind -- confirmed directly: clicking
  // either button at the daily limit generated and downloaded a real,
  // complete document. logUsageNow() gates each button's own click
  // directly, same pattern as Merge PDF's own discrete action.
  const { logUsageNow } = useLogUsageOnAction();


  return (
    <ToolContainer>
        <div className="max-w-md mx-auto space-y-4">
          <div><Label>Client Name *</Label><Input aria-label="Client Name" value={clientName} onChange={e => setClientName(e.target.value)} /></div>
          <div><Label>Service Provider Name *</Label><Input aria-label="Service Provider Name" value={providerName} onChange={e => setProviderName(e.target.value)} /></div>
          <div><Label>Description of Services *</Label><Textarea aria-label="Description of Services" value={serviceDescription} onChange={e => setServiceDescription(e.target.value)} className="min-h-[80px]" /></div>
          <div><Label>Payment Amount ($) *</Label><Input aria-label="Payment Amount ($)" type="number" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} /></div>
          <div><Label>Payment Terms</Label><Input aria-label="Payment Terms" value={paymentTerms} onChange={e => setPaymentTerms(e.target.value)} /></div>
          <div>
            <Label>Who owns the finished work?</Label>
            <select className="w-full border border-border rounded-md bg-background px-2 py-2 text-sm" value={ip} onChange={e => setIp(e.target.value as "client" | "provider")}>
              <option value="client">Client owns it once paid in full</option>
              <option value="provider">Provider keeps ownership; Client gets a license</option>
            </select>
          </div>
          <div><Label>State *</Label><Input aria-label="State" value={state} onChange={e => setState(e.target.value)} /></div>
          {missingRequired && (
            <NoticeBox className="text-xs">Fields marked * are required before you can preview or download.</NoticeBox>
          )}

          <Button variant="outline" onClick={() => setShowPreview(v => !v)} disabled={missingRequired} className="w-full gap-2">
            {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {showPreview ? "Hide Preview" : "Preview"}
          </Button>

          {showPreview && !missingRequired && (
            <LegalDocPreview title={TITLE} sections={buildSections()} />
          )}

          <div className="flex gap-2">
            <Button onClick={async () => { const allowed = await logUsageNow(); if (!allowed) return; generateLegalDoc(TITLE, buildSections(), "service-agreement.docx"); }} disabled={missingRequired} className="flex-1 gap-2">
              <Download className="h-4 w-4" /> Word
            </Button>
            <Button onClick={async () => { const allowed = await logUsageNow(); if (!allowed) return; generateLegalDocPdf(TITLE, buildSections(), "service-agreement.pdf"); }} disabled={missingRequired} variant="secondary" className="flex-1 gap-2">
              <Download className="h-4 w-4" /> PDF
            </Button>
          </div>
        </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} disclaimer="This tool generates a general-purpose service agreement template. It isn't legal advice and may not account for the specific laws of your state, country, or situation — requirements for this type of service agreement can vary significantly by jurisdiction. Have any document reviewed by a qualified attorney before you sign it or rely on it, especially for anything involving significant money, property, or legal obligations." />
    </ToolContainer>
  );
}
